import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tooltip from "@mui/material/Tooltip";
import { visuallyHidden } from "@mui/utils";

import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import DataSourceClass from "../../../service/dataSource";

import { KMKDialog } from "../../Common";
import { convertTimezone } from "../../../util/common";

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    "& .MuiPaper-root.MuiDialog-paper": {
      maxWidth: "960px",
    },
  },
  formContainer: {
    display: "grid",
    width: "100%",
    textAlign: "left",
    minHeight: "100px",
  },
  container: {
    padding: "24px 24px 24px 24px",
    backgroundColor: "#FFFFFF",
    minHeight: "100px",
  },
  Toolbar: {
    width: "100%",
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  DataTableContainer: {
    borderRadius: 4,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    "& ::-webkit-scrollbar-track": {
      marginTop: "36px",
    },
  },
  TableHead: {
    fontWeight: "bold",
    "& .MuiTableCell-root": {
      color: "rgba(0, 0, 0, 0.87)",
      padding: "6px 16px",
      height: "37px",
    },
  },
  TableBody: {
    "& .MuiOutlinedInput-input": {
      padding: "4px 10px",
    },
    "& .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
      wordBreak: "break-word",
    },
  },
  TableRow: {
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0,
    },
    "& .MuiTableCell-root": {
      border: "none",
    },
    "& .MuiTableCell-root:last-child": {
      padding: "10px 0px",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "24px",
    },
  },
  TableRowError: {
    color: theme.palette.error.main,
  },
  TableCheckbox: {
    align: "left",
    "&.Mui-disabled": {
      pointerEvents: "auto",
      cursor: "not-allowed",
    },
  },
  searchBox: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "end",
    marginBottom: "15px",
    p: 0,
  },
}));

const columns = [
  { id: "SCRIPT_STEP", label: "Step", width: "550px", align: "left" },
  { id: "LOAD_DATE", label: "Time", width: "250px", align: "left", sortable: true, disablePadding: true },
];

function DataTableHead(props) {
  const { columns, order, orderBy, onRequestSort, hidden, classes } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead style={hidden ? { visibility: "collapse" } : {}}>
      <TableRow>
        {columns.map((headCell, index) => (
          <TableCell
            key={index}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={order}
            style={{
              width: headCell.width,
            }}
            className={classes.TableHead}
          >
            {!headCell.sortable ? (
              headCell.label
            ) : (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const ApplyWarehouseHistory = ({ WorkspaceName, handleClose, configId }) => {
  const { Workspace_id } = useParams();

  const { classes } = useStyles();

  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [orderBy, setOrderBy] = useState("LOAD_DATE");
  const [sortDirection, setSortDirection] = useState("asc");

  const loadData = async (options) => {
    setLoading(true);
    const datasource = new DataSourceClass();
    const response = await datasource.getChangeHistory(Workspace_id, configId, {
      orderBy,
      sortDirection,
      search,
      ...options,
    });

    if (response.success) {
      setData(response.data.historyList);
    }

    setLoading(false);
    setDataLoaded(true);
  };

  useEffect(() => {
    setLoading(true);
    const datasource = new DataSourceClass();
    datasource.getChangeHistory(Workspace_id, configId, { orderBy, sortDirection, search }).then((response) => {
      if (response.success) {
        setData(response.data.historyList);
      }

      setLoading(false);
      setDataLoaded(true);
    });
    // Adding orderBy and other params was sending two api calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Workspace_id, configId]);

  const downloadSql = () => {
    const datasource = new DataSourceClass();
    datasource.downloadAppliedConfid(Workspace_id, configId, `VORTEX_${WorkspaceName} ConfigID-${configId}.sql`);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && sortDirection === "asc";
    let tmpOrder = isAsc ? "desc" : "asc";

    setSortDirection(tmpOrder);
    setOrderBy(property);

    loadData({
      orderBy: property,
      sortDirection: tmpOrder,
    });
  };

  const resetFilters = () => {
    setSortDirection("asc");
    setOrderBy("LOAD_DATE");
    setSearch("");

    loadData({
      orderBy: "LOAD_DATE",
      sortDirection: "asc",
      search: "",
    });
  };

  return (
    <>
      <Backdrop
        sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading && !dataLoaded}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      <KMKDialog className={classes.dialogPaper} open={dataLoaded} onClose={() => handleClose()}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar className={classes.Toolbar}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <ListAltIcon sx={{ mr: "10px" }} /> View Details (#{configId})
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => handleClose()}
              sx={{
                color: "black",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <DialogContent sx={{ m: 0, p: 0 }}>
          <Box sx={{ width: "100%" }} className={classes.container}>
            <Box sx={{ width: "100%" }} component="form" className={classes.formContainer}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Box className={classes.searchBox}>
                    <TextField
                      variant="standard"
                      placeholder="Search Step"
                      margin="normal"
                      value={search}
                      id="searchStep"
                      InputProps={{
                        startAdornment: <SearchIcon color="primary" sx={{ marginRight: "5px" }} />,
                        ...(search && {
                          endAdornment: (
                            <IconButton
                              onClick={() => {
                                setSearch("");
                                loadData({ search: "" });
                              }}
                              size="small"
                            >
                              <ClearIcon color="primary" />
                            </IconButton>
                          ),
                        }),
                      }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          loadData({ search: e.target.value });
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => setSearch(e.target.value)}
                      sx={{ mr: "5px", my: 0, width: { xs: "100%", sm: "40%" } }}
                    />
                    <Tooltip placement="top" title="Reset data">
                      <IconButton color="info" onClick={resetFilters}>
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip placement="top" title="Download SQL Script">
                      <IconButton color="info" onClick={downloadSql}>
                        <FileDownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Paper
                    sx={{ width: "100%", overflow: "hidden", border: "1px solid rgba(0,0,0,0.1)" }}
                    className={classes.DataTableContainer}
                  >
                    <TableContainer
                      sx={{
                        height: "350px",
                        width: "765px",
                        flexGrow: 1,
                      }}
                    >
                      <Table stickyHeader aria-label="sticky table" size="small">
                        <DataTableHead
                          columns={columns}
                          order={sortDirection}
                          orderBy={orderBy}
                          onRequestSort={handleRequestSort}
                          rowCount={data.length}
                          classes={classes}
                        />

                        <TableBody className={classes.TableBody}>
                          {loading ? (
                            <TableRow className={classes.TableRow} sx={{ height: 300 }}>
                              <TableCell colSpan={2} component="td" scope="row" align={"center"}>
                                <CircularProgress />
                              </TableCell>
                            </TableRow>
                          ) : data.length ? (
                            data.map((history, i) => (
                              <TableRow key={i} className={classes.TableRow} hover={true}>
                                <TableCell align="left" className={history.STATUS === 0 ? classes.TableRowError : ""}>
                                  {history.SCRIPT_STEP}
                                </TableCell>
                                <TableCell align="left" className={history.STATUS === 0 ? classes.TableRowError : ""}>
                                  {history?.LOAD_DATE ? convertTimezone(history.LOAD_DATE) : ""}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow className={classes.TableRow} sx={{ height: 300 }}>
                              <TableCell colSpan={5} component="td" scope="row" align={"center"}>
                                No records found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
      </KMKDialog>
    </>
  );
};

export default ApplyWarehouseHistory;
